import type { FeaturePrototype } from '../prototype/FeaturePrototype';
import type { FeatureCommandDependencyInfo } from './FeatureCommandAdapter';
import {
    featureCommandAdapterRegistry,
    type FeatureCommandAdapterRegistry,
} from './FeatureCommandAdapterRegistry';

/**
 * 特征命令依赖索引输入。
 */
export interface FeatureCommandDependencyIndexInput {
    /** 完整且按执行顺序排列的特征命令。 */
    readonly commands: readonly CadCommand[];
    /** 当前 Worker 正式对象，用于补全已经落入结果元数据的输入修订。 */
    readonly objects: readonly GeometryObject[];
}

/**
 * 命令历史中的统一特征依赖条目。
 */
export interface FeatureCommandDependencyEntry {
    /** 特征命令。 */
    readonly command: CadCommand;
    /** 命令在完整历史中的索引。 */
    readonly commandIndex: number;
    /** 从命令恢复出的特征原型。 */
    readonly prototype: FeaturePrototype;
    /** 特征种类。 */
    readonly featureKind: string;
    /** 来源草图 ID。 */
    readonly sourceSketchId: string | null;
    /** 适配器解析出的模型输入与产物。 */
    readonly dependencyInfo: FeatureCommandDependencyInfo;
}

/**
 * 特征命令依赖索引。
 *
 * 职责与边界：
 * 1. 统一把命令、原型绑定和 Worker 结果元数据投影成有序依赖条目；
 * 2. 为编辑、删除等历史操作解析“目标特征及其真实下游”的传递闭包；
 * 3. 不执行 Worker replay、Viewer mutation 或场景资源管理；
 * 4. 未注册适配器的命令不属于特征依赖图，由其各自命令系统继续管理。
 */
export class FeatureCommandDependencyIndex {
    /** 按完整命令历史顺序排列的特征条目。 */
    private readonly entries: FeatureCommandDependencyEntry[];

    /**
     * @param input 依赖索引输入。
     * @param adapterRegistry 命令适配器注册表，测试或扩展场景可注入独立注册表。
     */
    public constructor(
        input: FeatureCommandDependencyIndexInput,
        adapterRegistry: FeatureCommandAdapterRegistry = featureCommandAdapterRegistry,
    ) {
        this.entries = input.commands.flatMap((command, commandIndex) => {
            const adapter = adapterRegistry.resolveAdapter(command);
            const prototype = adapter?.createPrototypeFromCommand(command) ?? null;
            if (!adapter || !prototype) {
                return [];
            }

            return [{
                command,
                commandIndex,
                prototype,
                featureKind: prototype.getFeatureKind(),
                sourceSketchId: prototype.getSourceSketchId(),
                dependencyInfo: adapter.collectDependencyInfo({
                    command,
                    prototype,
                    objects: input.objects,
                    commands: input.commands,
                    commandIndex,
                }),
            }];
        });
    }

    /**
     * 读取全部依赖条目。
     *
     * @returns 保持命令历史顺序的浅副本；条目内容只读。
     */
    public getEntries(): FeatureCommandDependencyEntry[] {
        return [...this.entries];
    }

    /**
     * 解析目标特征及其传递下游。
     *
     * 设计说明：
     * 1. 只允许历史位置晚于目标的命令成为下游，避免异常反向引用形成循环；
     * 2. 同时匹配特征 ID 与适配器声明的产物 ID，兼容结果对象 ID 不等于命令 ID 的情况；
     * 3. 返回顺序始终与命令历史一致，可直接用于稳定 replay 和场景增量同步。
     *
     * @param targetFeatureId 目标特征 ID。
     * @returns 目标特征及全部传递下游 ID；目标不存在时返回空数组。
     */
    public resolveDependentFeatureIds(targetFeatureId: string, includeRebuildDependencies = false): string[] {
        const targetEntry = this.entries.find((entry) => entry.command.id === targetFeatureId) ?? null;
        if (!targetEntry) {
            return [];
        }

        const affectedFeatureIds = new Set<string>([targetEntry.command.id]);
        const affectedProducedObjectIds = new Set<string>(targetEntry.dependencyInfo.producedObjectIds);
        let dependencyAdded = true;

        while (dependencyAdded) {
            dependencyAdded = false;
            this.entries
                .filter((entry) => entry.commandIndex > targetEntry.commandIndex)
                .forEach((entry) => {
                    if (affectedFeatureIds.has(entry.command.id)) {
                        return;
                    }
                    const referenceIds = includeRebuildDependencies
                        ? [...entry.dependencyInfo.referencedModelIds, ...(entry.dependencyInfo.rebuildReferencedModelIds ?? [])]
                        : entry.dependencyInfo.referencedModelIds;
                    const dependsOnAffectedFeature = referenceIds
                        .some((referenceId) => (
                            affectedFeatureIds.has(referenceId)
                            || affectedProducedObjectIds.has(referenceId)
                        ));
                    if (!dependsOnAffectedFeature) {
                        return;
                    }

                    affectedFeatureIds.add(entry.command.id);
                    entry.dependencyInfo.producedObjectIds.forEach((objectId) => {
                        affectedProducedObjectIds.add(objectId);
                    });
                    dependencyAdded = true;
                });
        }

        return this.entries
            .filter((entry) => affectedFeatureIds.has(entry.command.id))
            .map((entry) => entry.command.id);
    }
}
